/**
 * @jsx jsx
 * @jsxRuntime classic
 * @jsxFrag React.Fragment
 */
import { jsx, cssMap } from '@compiled/react';
import React, { Fragment, useEffect, useState } from 'react';
import {
	type AnalyticsEventPayload,
	type UIAnalyticsEvent,
	type WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { LinkButton } from '@atlaskit/button/new';
import { injectIntl, type IntlShape, FormattedMessage } from 'react-intl-next';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	NAVIGATION_CHANNEL,
	OPERATIONAL_EVENT_TYPE,
	UI_EVENT_TYPE,
	withAnalyticsEvents,
} from '../../common/utils/analytics';
import { getLoginUrl } from '../../common/utils/environment';
import { errorToReason, type Reason } from '../../common/utils/error-to-reason';
import messages from '../../common/utils/messages';
import { Product } from '../../types';
import { SwitcherLoadError } from '../../common/utils/errors/switcher-load-error';
import { FETCH_ERROR_NAME } from '../../common/utils/errors/fetch-error';
import TRELLO_ERROR_SRC from '../../assets/trello-error';
import UNVERIFIED_ERROR_SRC from '../../assets/unverified-error';

const TRIGGER_SUBJECT = 'errorBoundary';
const ACTION_SUBJECT = 'rendered';

const styles = cssMap({
	robotsImg: {
		height: '200px',
	},
	trelloErrorImg: {
		width: '140px',
		height: '88px',
	},
	unverifiedUserImg: {
		height: '150px',
	},
	errorBoundaryBox: {
		display: 'flex',
		flex: 1,
		width: '343px',
		boxSizing: 'border-box',
		justifyContent: 'center',
		flexDirection: 'column',
		marginTop: token('space.600'),
		marginBottom: token('space.600'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	errorContent: {
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'column',
		textAlign: 'center',
	},
	errorHeading: {
		marginTop: token('space.400'),
	},
	errorMessage: {
		marginTop: token('space.300'),
	},
	loginButton: {
		marginTop: token('space.300'),
		justifyContent: 'center',
		alignSelf: 'stretch',
	},
});

interface ErrorBoundaryProps {
	children: React.ReactNode;
	extraAnalyticProps?: object;
	hideFallbackUI?: boolean;
	intl: IntlShape;
	onErrorCallback?: (error: Error) => void;
	product: Product;
	triggerSubject?: string;
}

type ErrorBoundaryState = {
	hasError: boolean;
	reason?: Reason;
};

const ErrorMessage = (props: { children: React.ReactNode }) => (
	<Box xcss={styles.errorMessage}>
		<Text as="p">{props.children}</Text>
	</Box>
);

class ErrorBoundary extends React.Component<
	ErrorBoundaryProps & WithAnalyticsEventsProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = { hasError: false };

	fireOperationalEvent = (payload: AnalyticsEventPayload) => {
		if (this.props.createAnalyticsEvent) {
			this.props
				.createAnalyticsEvent({
					eventType: OPERATIONAL_EVENT_TYPE,
					actionSubject: this.props.triggerSubject || TRIGGER_SUBJECT,
					...payload,
				})
				.fire(NAVIGATION_CHANNEL);
		}
	};

	componentDidCatch(error: any) {
		const reason = error instanceof SwitcherLoadError ? error.reason() : errorToReason(error);

		this.setState(
			{
				hasError: true,
				reason,
			},
			() => {
				this.fireOperationalEvent({
					action: ACTION_SUBJECT,
					attributes: {
						...reason,
						...this.props.extraAnalyticProps,
					},
				});
			},
		);

		this.props.onErrorCallback?.(error);
	}

	handleLogin = (_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		analyticsEvent
			.update({
				eventType: UI_EVENT_TYPE,
				actionSubjectId: 'login',
			})
			.fire(NAVIGATION_CHANNEL);
	};

	renderErrorBody() {
		const { reason } = this.state;

		if (!reason) {
			return;
		}

		if (reason.name === FETCH_ERROR_NAME) {
			if (reason.status === 401) {
				// Not authorised http error
				return (
					<Fragment>
						<ErrorMessage>
							<FormattedMessage {...messages.errorTextLoggedOut} />
						</ErrorMessage>
						<Box xcss={styles.loginButton}>
							<LinkButton href={getLoginUrl(this.props.product)} onClick={this.handleLogin}>
								<FormattedMessage {...messages.login} />
							</LinkButton>
						</Box>
					</Fragment>
				);
			}

			if (reason.status === 403) {
				return (
					<ErrorMessage>
						<FormattedMessage
							{...(fg('product-terminology-refresh')
								? messages.errorTextUnverifiedAppify
								: messages.errorTextUnverified)}
						/>
					</ErrorMessage>
				);
			}

			return (
				<ErrorMessage>
					<FormattedMessage {...messages.errorTextNetwork} />
				</ErrorMessage>
			);
		}

		return (
			<ErrorMessage>
				<FormattedMessage {...messages.errorText} />
			</ErrorMessage>
		);
	}

	renderErrorHeading() {
		const { reason } = this.state;

		if (!reason) {
			return;
		}

		if (reason.name === FETCH_ERROR_NAME && reason.status === 401) {
			return <FormattedMessage {...messages.errorHeadingLoggedOut} />;
		}

		if (reason.name === FETCH_ERROR_NAME && reason.status === 403) {
			return <FormattedMessage {...messages.errorHeadingUnverified} />;
		}

		return <FormattedMessage {...messages.errorHeading} />;
	}

	renderErrorImage() {
		const { intl, product } = this.props;
		const { reason } = this.state;

		if (product === Product.TRELLO) {
			return (
				<img
					css={styles.trelloErrorImg}
					src={TRELLO_ERROR_SRC}
					alt={intl.formatMessage(messages.errorImageAltText)}
				/>
			);
		}

		if (reason!.status === 403) {
			return (
				<img
					css={styles.unverifiedUserImg}
					src={UNVERIFIED_ERROR_SRC}
					alt={intl.formatMessage(messages.errorImageAltText)}
				/>
			);
		}

		return (
			<RobotImage css={styles.robotsImg} alt={intl.formatMessage(messages.errorImageAltText)} />
		);
	}

	render() {
		const { hideFallbackUI, children } = this.props;
		const { hasError } = this.state;

		if (hasError && hideFallbackUI) {
			return <></>;
		}

		if (hasError) {
			return (
				<div css={styles.errorBoundaryBox} data-testid="error-boundary">
					<div css={styles.errorContent}>
						{this.renderErrorImage()}
						<Box xcss={styles.errorHeading}>
							<Heading size="medium">{this.renderErrorHeading()}</Heading>
						</Box>
						{this.renderErrorBody()}
					</div>
				</div>
			);
		}

		return children;
	}
}

const RobotImage: React.ComponentType<React.ComponentProps<'img'>> = (props) => {
	const [imgSrc, setImgSrc] = useState<string | undefined>();
	useEffect(() => {
		import(
			/* webpackChunkName: "@ak-switcher-chunk-robot-error-img" */ '../../assets/robot-error'
		).then((src) => setImgSrc(src.default));
	}, []);
	return <img alt="" {...props} src={imgSrc} />;
};

export default withAnalyticsEvents()(injectIntl(ErrorBoundary));
